import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { WindowRefService } from '../../services/window-ref.service';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Subject } from 'rxjs';
import { filter, first, map, publishReplay, refCount, take, takeUntil } from 'rxjs/operators';
import { loadAccounts } from 'src/app/feature-modules/accounts/store/accounts.action';
import { selectAccounts, selectAccountsLoaded } from 'src/app/feature-modules/accounts/store/accounts.selector';
import { creditHealthActions } from 'src/app/feature-modules/credit-health/store/credit-health.action';
import { selectCreditHealthLoaded } from 'src/app/feature-modules/credit-health/store/credit-health.selector';
import { customerAttributesActions } from 'src/app/feature-modules/customer-attributes/store/customer-attributes.actions';
import { selectCustomerAttributesLoaded } from 'src/app/feature-modules/customer-attributes/store/customer-attributes.selector';
import { WelcomeModalComponent } from 'src/app/feature-modules/customer-hub/components/welcome-modal/welcome-modal.component';
import { getFeatureFlagEnabledByName$ } from 'src/app/utils/feature-flag.util';
import { maintenanceNotification } from '../../config/notifications.config';
import { getAutoPayActions } from '../../feature-modules/accounts-notifications/store/accounts-notifications.action';
import { loadHubOffers } from '../../feature-modules/offers/store/actions/offers.actions';
import {
  selectDismissHubOffer,
  selectHubOffers,
  selectHubOffersIsLoaded,
} from '../../feature-modules/offers/store/selectors/offers.selectors';
import { getOptInActions } from '../../feature-modules/opt-in/store/opt-in.actions';
import { selectOptInIsLoaded } from '../../feature-modules/opt-in/store/opt-in.selectors';
import { getPaperlessActions, getProfileResidenceActions } from '../../feature-modules/profile/store/actions/profile.actions';
import {
  selectProfilePaperlessIsLoading,
  selectProfileResidenceProcessed,
} from '../../feature-modules/profile/store/selectors/profile.selectors';
import { HubCardVerificationModalComponent } from '../../feature-modules/shared/components/hub-card-verification-modal/hub-card-verification-modal.component';
import { AuthService } from '../../services/auth.service';
import { getHubFeatureFlagActions } from '../../store/actions/feature-flag.action';
import {
  selectHubFeatureFlags,
  selectHubFeatureFlagsProcessed,
  selectHubIsDisplayingMaintenanceBanner,
} from '../../store/selectors/feature-flag.selector';
import { waitForSourceData } from '../../utils/rxjs-util';

@Component({
    selector: 'app-customer-hub-layout',
    templateUrl: './customer-hub-layout.component.html',
    styleUrls: ['./customer-hub-layout.component.scss', '../../app.component.scss'],
    standalone: false
})
export class CustomerHubLayoutComponent implements OnInit {
  private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
  accounts$ = this.store.select(selectAccounts).pipe(publishReplay(1), refCount());
  displayStickyBanner$ = combineLatest([
    this.store.select(selectHubOffers),
    this.store.select(selectDismissHubOffer),
    this.store.select(selectHubFeatureFlags).pipe(getFeatureFlagEnabledByName$('AWEE491CrossSellStickyBanner')),
  ]).pipe(map(([offers, dismissedOffer, featureFlag]) => offers?.length > 0 && !dismissedOffer && featureFlag));

  isLoaded$ = combineLatest([
    this.store.select(selectCustomerAttributesLoaded),
    this.store.select(selectAccountsLoaded),
    this.store.select(selectOptInIsLoaded),
    this.store.select(selectCreditHealthLoaded),
    // paperless might not load at all if it isn't relevant; so only consider it loaded if it isn't loading
    this.store.select(selectProfilePaperlessIsLoading).pipe(map((isLoading) => !isLoading)),
    this.store.select(selectHubOffersIsLoaded),
    this.store.select(selectProfileResidenceProcessed),
    this.store.select(selectHubFeatureFlagsProcessed).pipe(
      filter((isLoaded) => isLoaded),
      first()
    ),
  ]).pipe(
    map((loadingStates) => loadingStates.every((isLoaded) => isLoaded)),
    takeUntil(this.ngUnsubscribe$)
  );

  private featureFlags$ = this.store.select(selectHubFeatureFlags);
  displayMaintenanceBanner$ = waitForSourceData(
    [this.featureFlags$],
    this.store.select(selectHubIsDisplayingMaintenanceBanner)
  );

  maintenanceMessageHtml = maintenanceNotification;
  cid: string;
  idToken: string;
  private isLaunchingCardVerificationModalRouterState: boolean;

  constructor(
    private readonly store: Store,
    private readonly cookieService: CookieService,
    private readonly authService: AuthService,
    private readonly windowRefService: WindowRefService,
    public dialog: MatDialog
  ) {
    // TODO: comment back in once we resolve card verification infinite loop (we need to somehow clear router state after modal close)
    // this.isLaunchingCardVerificationModalRouterState =
    //   this.router.getCurrentNavigation()?.extras?.state?.isLaunchingCardVerificationModal;
  }
  async ngOnInit() {
    const { cid, idToken } = await this.authService.handleCurrentSessionRetrieval();
    this.cid = cid;
    this.idToken = idToken;

    this.store.dispatch(customerAttributesActions.start({ request: {} }));
    this.store.dispatch(loadAccounts());
    this.store.dispatch(getOptInActions.start({ request: null }));
    this.store.dispatch(creditHealthActions.start({ request: { cid: this.cid } }));
    this.store.dispatch(getHubFeatureFlagActions.start({ request: 'hub' }));
    this.store.dispatch(getProfileResidenceActions());

    this.featureFlags$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe(() => {
        // due to some backend syncing issue, we need to wait for feature flags before calling offers
        this.store.dispatch(loadHubOffers({ source: 'hub' }));
      });

    this.accounts$
      .pipe(
        filter((v) => !!v),
        take(1)
      )
      .subscribe((accounts) => {
        if (accounts.loanAccounts[0]?.accountDetailsList.length > 0 && accounts.loanAccounts[0].url) {
          const customerId = new URL(accounts.loanAccounts[0].url).searchParams.get('domain');
          this.store.dispatch(getPaperlessActions.start({ request: parseInt(customerId) }));
        }
      });

    combineLatest([this.accounts$.pipe(filter((accounts) => !!accounts)), this.displayMaintenanceBanner$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([accounts, displayMaintenanceBanner]) => {
        if (!displayMaintenanceBanner && accounts.loanAccounts[0]?.accountDetailsList?.length) {
          const loanAccounts = accounts.loanAccounts[0].accountDetailsList;
          const customerId = accounts.loanAccounts[0].url.replace(/\D/g, '');
          const autoPayRequest = loanAccounts.map((account) => {
            return {
              accountId: account.id,
            };
          });
          this.store.dispatch(getAutoPayActions.start({ request: { customerId, accounts: autoPayRequest } }));
        }
      });

    // Leaving this commented out until card verification infinite loop issue from before
    // hub optimization is resolved
    // combineLatest([
    //   this.isLoaded$,
    //   this.store.select(selectAccounts)
    // ]).pipe(
    //   filter(([isLoaded, accounts]) => isLoaded === true && !!accounts),
    //   takeUntil(this.ngUnsubscribe$)
    // ).subscribe(([,accounts]) => {
    //   if (this.isLaunchingCardVerificationModalRouterState && accounts?.cardAccounts?.[0]?.lastFourCardNumber) {
    //     this.openCardVerificationModal(accounts.cardAccounts[0].lastFourCardNumber);
    //   } else if (!this.cookieService.check('has_seen_hub_welcome_modal')) {
    //     this.openWelcomeModal();
    //   }
    // })
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  openCardVerificationModal(lastFourCardNumber: string) {
    setTimeout(() => {
      this.dialog.open(HubCardVerificationModalComponent, {
        maxWidth: '529px',
        data: {
          lastFourCardNumber: lastFourCardNumber,
          idToken: this.cookieService.get('X-Access-Token').split('Bearer ')[1],
        },
      });
    }, 500);
  }

  openWelcomeModal() {
    setTimeout(() => {
      this.dialog.open(WelcomeModalComponent);
    }, 500);
  }
}
